/**
 * /embudos — la lista de embudos, que es por donde entra el módulo.
 *
 * Antes el módulo abría directo en el tablero del primer embudo y el único
 * lugar donde se podía crear otro era el estado vacío — que deja de existir
 * apenas hay uno. Con un embudo creado no había forma de hacer el segundo.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Recorrido } from '../components/ui/Recorrido.js';
import { useRecorrido } from '../hooks/useRecorrido.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { Modal } from '../components/ui/Modal.js';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { Pill, PillGroup } from '../components/PillGroup.js';
import { useToast } from '../components/ui/Toast.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { formatMoney } from '../lib/money.js';
import { matchesSearch } from '../lib/searchTerms.js';
import { useFunnels, useCreateFunnel, useDeleteFunnel } from '../hooks/useFunnels.js';
import { Checkbox } from '../components/ui/Checkbox.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';

// Columna de cotejo idéntica a Formularios/Proyectos/Leads: mismo ritmo entre listas.
const COLS = 'var(--evo-table-check-col) minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';

const HEAD_CELL = {
  padding: 'var(--evo-table-head-py) var(--evo-table-cell-px)',
  display: 'flex',
  alignItems: 'center',
  color: 'var(--evo-fg-3)',
  fontSize: 'var(--evo-list-header-fs)',
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: 'var(--evo-ls-caps)',
  whiteSpace: 'nowrap' as const,
};

const CELL = {
  padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px)',
  display: 'flex',
  alignItems: 'center',
  fontSize: 'var(--evo-list-subtitle-fs)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

/**
 * Alta de embudo. Solo pide el nombre: las etapas las pone el servidor en el
 * idioma de la cuenta y se editan después, que es cuando se sabe cuáles son.
 */
export function CreateFunnelModal({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated?: (id: number) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const create = useCreateFunnel();
  const [name, setName] = useState('');

  useEffect(() => { if (open) setName(''); }, [open]);

  async function submit() {
    try {
      const funnel = await create.mutateAsync({ name: name.trim() });
      onClose();
      onCreated?.(funnel.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('funnels.newFunnel')}
      size="sm"
      preventClose={create.isPending}
      footer={
        <>
          <Button variant="outline" tone="muted" onClick={onClose} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          <Button tone="cyan" loading={create.isPending} disabled={!name.trim()} onClick={() => { void submit(); }}>
            {t('common.create')}
          </Button>
        </>
      }
    >
      <Input label={t('funnels.editor.name')} value={name} onChange={setName} fullWidth />
    </Modal>
  );
}

export function FunnelsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setOverride } = useTopBarCtx();
  const { data, isLoading } = useFunnels();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const toast = useToast();
  const borrar = useDeleteFunnel();
  const { confirm, dialog: confirmDialog } = useConfirm();
  // Cotejos y barra de acciones (2026-10-12): hasta acá no había forma de
  // borrar un embudo desde la pantalla, aunque el servidor lo permitía.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [borrando, setBorrando] = useState(false);

  function toggleSelect(id: number) {
    setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }

  async function handleBulkDelete() {
    const elegidos = (data?.funnels ?? []).filter(f => selected.has(f.id));
    if (elegidos.length === 0) return;
    // Borrar un embudo se lleva TODAS sus cartas: la confirmación lo dice con
    // el número, y el servidor deja una copia antes de borrar.
    const cartas = elegidos.reduce((s, f) => s + f.opportunityCount, 0);
    const ok = await confirm({
      title: t('funnels.list.bulk.deleteTitle', { count: elegidos.length }),
      description: cartas > 0
        ? t('funnels.list.bulk.deleteBody', { count: cartas })
        : t('funnels.list.bulk.deleteBodyEmpty'),
      confirmLabel: t('funnels.list.bulk.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    setBorrando(true);
    const resultados = await Promise.allSettled(elegidos.map(f => borrar.mutateAsync(f.id)));
    setBorrando(false);
    const hechos = resultados.filter(r => r.status === 'fulfilled').length;
    setSelected(new Set());
    if (hechos === elegidos.length) toast.success(t('funnels.list.bulk.deleted', { count: hechos }));
    else toast.error(t('funnels.list.bulk.deletePartial', { done: hechos, count: elegidos.length }));
  }

  useEffect(() => {
    setOverride({
      title: t('funnels.pageTitle'),
      heroIcon: 'funnel',
      heroIconColor: 'var(--evo-cyan)',
      bottomActions: (
        // Ancla del recorrido guiado. Un div y no una prop en Pill: ese
        // primitive tiene 196 usos y esto es identidad de una pantalla.
        <div data-tour-id="funnels-nuevo" style={{ display: 'flex' }}>
          <PillGroup size="md">
            <Pill tone="cyan" icon="plus" onClick={() => setShowCreate(true)}>
              {t('funnels.newFunnel')}
            </Pill>
          </PillGroup>
        </div>
      ),
    });
    return () => setOverride({});
  }, [setOverride, t]);

  const funnels = (data?.funnels ?? []).filter(f => matchesSearch(search, [f.name, f.description ?? '']));

  // ANTES del return condicional de abajo: un hook después de un early return
  // cambia la cantidad de hooks entre renders y React tira la pantalla entera.
  // Pasó en producción el 2026-09-11 — /embudos quedó en "Something went
  // wrong" hasta que se revirtió. Por eso `listo` recibe el estado de carga en
  // vez de que el hook no se llame mientras carga.
  const recorrido = useRecorrido('funnels', {
    hayDatos: (data?.funnels ?? []).length > 0,
    listo: !isLoading,
  });

  if (isLoading) return <LoadingState />;

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      <Recorrido
        pasos={recorrido.pasos}
        abierto={recorrido.abierto}
        onCerrar={recorrido.cerrar}
        onTerminar={recorrido.terminar}
      />
      <FilterRow tourId="funnels-toolbar">
        <FilterRow.Search value={search} onChange={setSearch} placeholder={t('common.search')} />
      </FilterRow>

      {/* SelectionActionBar a page root, full-width, post-FilterRow (REGLA #7). */}
      <SelectionActionBar
        visible={selected.size > 0}
        countLabel={t('funnels.list.bulk.selected', { count: selected.size })}
        onClearSelection={() => setSelected(new Set())}
      >
        <SelectionActionBar.Action
          label={t('funnels.list.bulk.delete')}
          tone="danger"
          loading={borrando}
          onClick={() => { void handleBulkDelete(); }}
        />
      </SelectionActionBar>

      <div className="flex-1 overflow-hidden" data-tour-id="funnels-lista" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {(data?.funnels ?? []).length === 0 ? (
          <EmptyState
            title={t('funnels.empty.title')}
            description={t('funnels.empty.body')}
            action={<Button tone="cyan" onClick={() => setShowCreate(true)}>{t('funnels.empty.action')}</Button>}
          />
        ) : (
          <>
            <div
              style={{
                display: 'grid', gridTemplateColumns: COLS,
                height: 'var(--evo-toolbar-height)',
                alignItems: 'center',
                borderBottom: '1px solid var(--evo-stroke)',
                flexShrink: 0,
                background: 'var(--evo-bg-2)',
              }}
            >
              <div style={{ padding: 'var(--evo-table-head-py) var(--evo-list-checkbox-gap) var(--evo-table-head-py) var(--evo-list-pad-x)' }}>
                <Checkbox
                  checked={funnels.length > 0 && funnels.every(f => selected.has(f.id))}
                  indeterminate={selected.size > 0 && !funnels.every(f => selected.has(f.id))}
                  onChange={() => {
                    const todos = funnels.length > 0 && funnels.every(f => selected.has(f.id));
                    setSelected(prev => {
                      const next = new Set(prev);
                      for (const f of funnels) { if (todos) next.delete(f.id); else next.add(f.id); }
                      return next;
                    });
                  }}
                />
              </div>
              <div style={{ ...HEAD_CELL, padding: 'var(--evo-table-head-py) var(--evo-table-cell-px) var(--evo-table-head-py) 0' }}>
                {t('funnels.list.colFunnel')}
              </div>
              <div data-tour-id="funnels-col-formulario" style={HEAD_CELL}>{t('funnels.list.colForm')}</div>
              <div data-tour-id="funnels-col-etapas" style={HEAD_CELL}>{t('funnels.list.colStages')}</div>
              <div style={HEAD_CELL}>{t('funnels.list.colOpen')}</div>
              <div data-tour-id="funnels-col-valor" style={{ ...HEAD_CELL, justifyContent: 'flex-end', padding: 'var(--evo-table-head-py) var(--evo-list-pad-x) var(--evo-table-head-py) var(--evo-table-cell-px)' }}>
                {t('funnels.list.colValue')}
              </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {funnels.map(funnel => (
                <div
                  key={funnel.id}
                  data-tour-id={funnel.id === funnels[0]?.id ? 'funnels-fila' : undefined}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/embudos/${funnel.id}`)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); navigate(`/embudos/${funnel.id}`); } }}
                  style={{
                    display: 'grid', gridTemplateColumns: COLS,
                    alignItems: 'flex-start',
                    minHeight: 'var(--evo-list-row-min-height)',
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--evo-stroke)',
                  }}
                >
                  <div
                    style={{ padding: 'calc(var(--evo-table-cell-py) + var(--evo-list-check-nudge)) var(--evo-list-checkbox-gap) var(--evo-table-cell-py) var(--evo-list-pad-x)' }}
                    onClick={e => { e.stopPropagation(); toggleSelect(funnel.id); }}
                    onKeyDown={e => { e.stopPropagation(); }}
                  >
                    <div style={{ pointerEvents: 'none' }}>
                      <Checkbox checked={selected.has(funnel.id)} onChange={() => {}} />
                    </div>
                  </div>

                  <div style={{ ...CELL, padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px) var(--evo-table-cell-py) 0', display: 'block' }}>
                    <div style={{ color: 'var(--evo-fg-1)', fontSize: 'var(--evo-list-title-fs)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {funnel.name}
                    </div>
                    {funnel.description && (
                      <div style={{ marginTop: '2px', color: 'var(--evo-fg-3)', fontSize: 'var(--evo-list-subtitle-fs)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {funnel.description}
                      </div>
                    )}
                  </div>

                  <div style={CELL}>
                    {funnel.forms.length > 0 ? (
                      <Pill variant="outline" tone="cyan" size="xs">
                        {funnel.forms.map(f => f.name).join(' · ')}
                      </Pill>
                    ) : (
                      <span style={{ color: 'var(--evo-fg-4)' }}>{t('funnels.list.noForm')}</span>
                    )}
                  </div>

                  <div style={{ ...CELL, color: 'var(--evo-fg-3)' }}>
                    {t('funnels.list.stagesCount', { count: funnel.stages.length })}
                  </div>

                  <div style={{ ...CELL, color: 'var(--evo-fg-2)', fontVariantNumeric: 'tabular-nums' }}>
                    {funnel.openCount}
                  </div>

                  <div style={{ ...CELL, justifyContent: 'flex-end', padding: 'var(--evo-table-cell-py) var(--evo-list-pad-x) var(--evo-table-cell-py) var(--evo-table-cell-px)' }}>
                    <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-1)', fontVariantNumeric: 'tabular-nums' }}>
                      {formatMoney(funnel.openTotalCents, 'usd')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {confirmDialog}

      <CreateFunnelModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={id => navigate(`/embudos/${id}`)}
      />
    </div>
  );
}
